import { FormikProvider, useFormik } from "formik";
import { FormHelper } from "../../../helpers/formHelper";
import { CountryCheckHelper } from "../../../helpers/countryCheckHelper";
import { useEffect, useRef, useState } from "react";
import { InvestorService } from "../../../pages/investor/investorService";
import { IS_SA } from "../../../config/appConfig";
import { getUser } from "../../../services/authService";
import { Box, Chip, TextField } from "@mui/material";
import { OTPAuthenticationDialog } from "../otpAuthenticationDialog";
import { OTPTypes } from "../../../constants/otpTypes";
import { SystemSettingsService } from "../../../services/systemSettingsService";
import { ConfirmPasswordDialog } from "../confirmPasswordDialog";
import { SectionWithHeading } from "../common/sectionWithHeading";
import { DialogWrapper } from "../common/dialogWrapper";
import { useTranslation } from "react-i18next";
import { InputWrapper } from "../formUtilities/useInput";
import SelectWrapper from "../formUtilities/useSelect";
import { useSelector } from "react-redux";
import { ButtonWrapper } from "../formUtilities/useButton";
import { FsButton } from "../button";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { Toaster } from "../common/toaster";
import { UploadIBANDocument } from "../uploadIbanDocumentDialog";

export const InvestorBankDetails = ({
  onSuccess = () => {},
  onCancel = () => {},
  isWithdraw = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [showOTPAuth, setShowOTPAuth] = useState(false);
  const [isEditInfo, setIsEditInfo] = useState(false);
  const [showIbanDialog, setShowIbanDialog] = useState(false);
  const [ibanInput, setIbanInput] = useState("");
  const [ibanLoading, setIbanLoading] = useState(false);
  const [showIbanDocumentDialog, setShowIbanDocumentDialog] = useState(false);
  const [ibanRequestId, setIbanRequestId] = useState(null);
  const investorService = new InvestorService();
  const systemSettingsService = new SystemSettingsService();
  const [bankDetails, setBankDetails] = useState(null);
  const pendingEditsRef = useRef(null);

  const isKsaInternational = IS_SA && getUser()?.isKsaInternationalInvestor === true;
  const { investorCollections } = useSelector(
    (state) => state.investorCollectionReducer
  );
  const bankCountries = investorCollections?.countryCollection || [];
  const { t } = useTranslation();
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const form = useFormik({
    initialValues: {
      accountName: null,
      bankName: null,
      bankCountry: null,
      iban: null,
      accountNumber: null,
      swiftCode: null,
      routingNumber: null,
    },
    ...FormHelper.formikProps(),
  });

  const settings = () => {
    return systemSettingsService.systemSettings();
  };

  useEffect(() => {
    getBankDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getBankDetails = (pendingEdits = null) => {
    setLoading(true);
    investorService
      .getBankDetails()
      .then((resp) => {
        setBankDetails(resp.data);
        let details = {
          ...resp.data,
          iban: resp.data?.ibanRequest?.iban ?? null,
          bankCountryId: resp.data?.bankCountry?.id
            ? resp.data?.bankCountry?.id
            : IS_SA
            ? 1
            : null,
        };
        if (pendingEdits) {
          details = { ...details, ...pendingEdits };
        }
        form.setValues(details);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const validateAustraliaBankDetails = () => {
    if (!CountryCheckHelper.ifAustralia(form.values?.bankCountryId, bankCountries)) return true;
    const accountNumber = (form.values?.accountNumber || "").replace(/\s+/g, "");
    if (!accountNumber) {
      Toaster({ msg: t("accountNumberRequiredForAustralia") || "Account number is required for Australian bank accounts.", isError: true });
      return false;
    }
    if (!/^\d{6,10}$/.test(accountNumber)) {
      Toaster({ msg: t("accountNumberMustBe6To10Digits") || "Account number must be 6 to 10 digits for Australian bank accounts.", isError: true });
      return false;
    }
    if (!(form.values?.swiftCode || "").trim()) {
      Toaster({ msg: t("swiftCodeRequiredForAustralia") || "SWIFT/BIC code is required for Australian bank accounts.", isError: true });
      return false;
    }
    return true;
  };

  const uploadUserBankDetails = () => {
    if (!validateAustraliaBankDetails()) return;
    setLoading(true);
    const userBankDetails = form.values;
    if (userBankDetails.iban) {
      userBankDetails.iban = userBankDetails.iban.replace(/\s+/g, "");
    }
    delete userBankDetails?.bankCountry;
    investorService
      .updateBankDetails(userBankDetails)
      .then(() => {
        setLoading(false);
        setIsEditInfo(false);
        getBankDetails();
        onSuccess();
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onBankDetailsSave = () => {
    if (!isKsaInternational && settings()?.enabledOtp) {
      setShowOTPAuth(true);
    } else {
      setShowPasswordDialog(true);
    }
  };

  const onPasswordDialogClose = (e) => {
    setShowPasswordDialog(false);
    if (e) {
      uploadUserBankDetails();
    } else {
      setLoading(false);
    }
  };

  const onCloseOTPAuth = (resp) => {
    setShowOTPAuth(false);
    if (resp) {
      uploadUserBankDetails();
    }
  };

  const submitIbanUpdate = () => {
    setIbanLoading(true);
    pendingEditsRef.current = (isEditInfo || isWithdraw)
      ? {
          accountName: form.values.accountName,
          bankName: form.values.bankName,
          bankCountryId: form.values.bankCountryId,
          accountNumber: form.values.accountNumber,
          swiftCode: form.values.swiftCode,
          routingNumber: form.values.routingNumber,
        }
      : null;
    investorService
      .updateIban({ iban: ibanInput.replace(/\s+/g, "") })
      .then((resp) => {
        setIbanLoading(false);
        setShowIbanDialog(false);
        if (resp.data?.documentRequested) {
          setIbanRequestId(resp.data.ibanRequestId);
          setShowIbanDocumentDialog(true);
        } else {
          getBankDetails(pendingEditsRef.current);
          pendingEditsRef.current = null;
        }
      })
      .catch(() => {
        setIbanLoading(false);
        pendingEditsRef.current = null;
      });
  };

  const getIbanStatus = () => {
    const req = bankDetails?.ibanRequest;
    if (!req || !req.iban) return null;
    if (req.approved) return { label: t("ibanStatusApproved"), color: "success" };
    if (req.documentRequested) return { label: t("ibanStatusDocumentRequired"), color: "warning" };
    return { label: t("ibanStatusPendingApproval"), color: "info" };
  };

  const onEdit = (e) => {
    e.preventDefault();
    if (isEditInfo) {
      onBankDetailsSave();
    } else {
      setIsEditInfo(!isEditInfo);
    }
  };

  return (
    <Box>
      <SectionWithHeading
        loading={loading}
        isEditButton={!isWithdraw}
        onEditInfo={(e) => {
          onEdit(e);
        }}
        isEditToggle={!isWithdraw ? isEditInfo : false}
        heading={
          <Box className="flex items-center">
            <span className="mx-2">{t("bankDetails")}</span>
          </Box>
        }
        children={
          <FormikProvider value={form}>
            <Box className="grid grid-flow-row gap-3">
              <Box className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <fieldset
                  disabled={!isWithdraw ? !isEditInfo : false}
                  style={{ display: "contents" }}
                >
                  <InputWrapper
                    disabled={!!bankDetails?.accountName}
                    key={"accountName"}
                    name={"accountName"}
                    label={"accountName"}
                  ></InputWrapper>
                  <InputWrapper
                    key={"bankName"}
                    name={"bankName"}
                    label={"bankName"}
                  ></InputWrapper>
                  <SelectWrapper
                    name={"bankCountryId"}
                    key={"bankCountryId"}
                    items={bankCountries}
                    label={"bankCountry"}
                  />
                </fieldset>
                <Box className="flex items-start gap-2">
                  <Box className="flex-1 flex flex-col gap-1">
                    <InputWrapper
                      key={"iban"}
                      name={"iban"}
                      label={"iban"}
                      disabled={true}
                    />
                    {getIbanStatus() && (
                      <Chip
                        size="small"
                        label={getIbanStatus().label}
                        color={getIbanStatus().color}
                        className="!w-fit"
                      />
                    )}
                  </Box>
                  <ButtonWrapper
                    variant={"outlined"}
                    classList="!mt-[20px] !min-w-[40px] !w-[40px] !h-[40px]"
                    label={<EditOutlinedIcon />}
                    onClick={() => {
                      setIbanInput(form.values.iban || "");
                      setShowIbanDialog(true);
                    }}
                  />
                  {bankDetails?.ibanRequest?.documentRequested && (
                    <ButtonWrapper
                      variant={"outlined"}
                      classList="!mt-[20px] !min-w-[40px] !w-[40px] !h-[40px]"
                      label={<CloudUploadOutlinedIcon />}
                      onClick={() => {
                        pendingEditsRef.current = (isEditInfo || isWithdraw)
                          ? {
                              accountName: form.values.accountName,
                              bankName: form.values.bankName,
                              bankCountryId: form.values.bankCountryId,
                              accountNumber: form.values.accountNumber,
                              swiftCode: form.values.swiftCode,
                              routingNumber: form.values.routingNumber,
                            }
                          : null;
                        setIbanRequestId(bankDetails.ibanRequest.ibanRequestId);
                        setShowIbanDocumentDialog(true);
                      }}
                    />
                  )}
                </Box>
                <fieldset
                  disabled={!isWithdraw ? !isEditInfo : false}
                  style={{ display: "contents" }}
                >
                  <InputWrapper
                    key={"accountNumber"}
                    name={"accountNumber"}
                    label={"accountNumber"}
                    onChange={(e) => {
                      const cleanValue = e.target.value.replace(/\s+/g, "");
                      form.setFieldValue("accountNumber", cleanValue);
                    }}
                  ></InputWrapper>
                  <InputWrapper
                    key={"swiftCode"}
                    name={"swiftCode"}
                    label={"swiftCode"}
                    onChange={(e) => {
                      const cleanValue = e.target.value.replace(/\s+/g, "");
                      form.setFieldValue("swiftCode", cleanValue);
                    }}
                  ></InputWrapper>
                  {CountryCheckHelper.ifUnitedStates(form.values?.bankCountryId, bankCountries) && (
                    <InputWrapper
                      key={"routingNumber"}
                      name={"routingNumber"}
                      label={"routingNumber"}
                      onChange={(e) => {
                        const cleanValue = e.target.value.replace(/\s+/g, "");
                        form.setFieldValue("routingNumber", cleanValue);
                      }}
                    ></InputWrapper>
                  )}
                </fieldset>
              </Box>
              {isWithdraw ? (
                <Box className="flex flex-row justify-center items-center">
                  <FsButton
                    loading={loading}
                    onClick={() => {
                      onCancel();
                    }}
                    variant="outline"
                  >{t("cancel")}</FsButton>
                  <FsButton
                    loading={loading}
                    onClick={() => {
                      onBankDetailsSave();
                    }}
                    className="!mx-3"
                  >{t("save")}</FsButton>
                </Box>
              ) : null}
            </Box>
          </FormikProvider>
        }
      ></SectionWithHeading>
      {showOTPAuth ? (
        <OTPAuthenticationDialog
          type={OTPTypes.Transaction}
          state={showOTPAuth}
          corrolationId={null}
          onClose={(e) => {
            onCloseOTPAuth(e);
          }}
        ></OTPAuthenticationDialog>
      ) : null}
      {showPasswordDialog ? (
        <ConfirmPasswordDialog
          state={showPasswordDialog}
          onClose={(e) => {
            onPasswordDialogClose(e);
          }}
        ></ConfirmPasswordDialog>
      ) : null}
      {showIbanDocumentDialog ? (
        <UploadIBANDocument
          state={showIbanDocumentDialog}
          ibanRequestId={ibanRequestId}
          onClose={() => {
            setShowIbanDocumentDialog(false);
            setIbanRequestId(null);
            getBankDetails(pendingEditsRef.current);
            pendingEditsRef.current = null;
          }}
        />
      ) : null}
      {showIbanDialog ? (
        <DialogWrapper
          state={showIbanDialog}
          title={t("updateIban")}
          maxWidth="sm"
          onClose={() => setShowIbanDialog(false)}
          content={
            <Box className="pt-3">
              <TextField
                fullWidth
                label={t("iban")}
                value={ibanInput}
                onChange={(e) => setIbanInput(e.target.value)}
                variant="outlined"
              />
            </Box>
          }
          actions={
            <Box className="flex gap-3">
              <FsButton
                variant="outline"
                onClick={() => setShowIbanDialog(false)}
              >{t("cancel")}</FsButton>
              <FsButton
                loading={ibanLoading}
                onClick={submitIbanUpdate}
              >{t("save")}</FsButton>
            </Box>
          }
        />
      ) : null}
    </Box>
  );
};
